import React, { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, useWindowDimensions, View } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Carousel, { Pagination } from 'react-native-reanimated-carousel';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import MapCarCard from '@/ui/map-car-card';

const CARD_INSET = 16;
const CARD_GAP = 8;
const HALF_GAP = CARD_GAP / 2;
const CARD_PEEK = 43;
const CARD_HEIGHT = 180;
const MAX_PAGINATION_DOTS = 4;
const NUDGE_DISTANCE = 28;

interface MapCarCarouselProps {
  cars: Car[];
  selectionKey: string;
  onCarPress: (car: Car) => void;
}

export const MapCarCarousel = ({
  cars,
  selectionKey,
  onCarPress,
}: MapCarCarouselProps) => {
  const { styles, theme } = useStyles(stylesheet);
  const { width: screenWidth } = useWindowDimensions();
  const carouselRef = useRef<ICarouselInstance>(null);
  const progress = useSharedValue(0);
  const nudgeX = useSharedValue(0);
  const isMultiCar = cars.length > 1;
  const itemWidth = screenWidth - CARD_INSET - CARD_PEEK;
  const car = cars[0];
  const [dotCount, setDotCount] = useState(
    Math.min(cars.length, MAX_PAGINATION_DOTS)
  );
  const dotsData: object[] = Array.from({ length: dotCount }, () => ({}));
  const page = useDerivedValue(() =>
    Math.floor(Math.round(progress.value) / MAX_PAGINATION_DOTS)
  );
  const dotsProgress = useDerivedValue(
    () => progress.value - page.value * MAX_PAGINATION_DOTS
  );

  useAnimatedReaction(
    () => page.value,
    (currentPage, previousPage) => {
      if (currentPage !== previousPage) {
        runOnJS(setDotCount)(
          Math.min(
            cars.length - currentPage * MAX_PAGINATION_DOTS,
            MAX_PAGINATION_DOTS
          )
        );
      }
    },
    [cars.length]
  );

  useEffect(() => {
    carouselRef.current?.scrollTo({ index: 0, animated: false });
    progress.value = 0;
    setDotCount(Math.min(cars.length, MAX_PAGINATION_DOTS));

    if (!isMultiCar) {
      nudgeX.value = 0;
      return;
    }

    let cancelled = false;

    const playNudge = (reduceMotion: boolean) => {
      if (cancelled || reduceMotion) {
        nudgeX.value = 0;
        return;
      }

      nudgeX.value = 0;
      nudgeX.value = withSequence(
        withTiming(-NUDGE_DISTANCE, {
          duration: 280,
          easing: Easing.out(Easing.cubic),
        }),
        withTiming(0, {
          duration: 420,
          easing: Easing.inOut(Easing.cubic),
        })
      );
    };

    AccessibilityInfo.isReduceMotionEnabled().then(playNudge);

    return () => {
      cancelled = true;
    };
  }, [cars.length, isMultiCar, nudgeX, progress, selectionKey]);

  const nudgeStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: nudgeX.value }],
  }));
  const centeredOffset = (screenWidth - itemWidth) / 2;

  if (!isMultiCar) {
    if (!car) {
      return null;
    }

    return (
      <View style={styles.singleCard}>
        <MapCarCard car={car} onPress={() => onCarPress(car)} />
      </View>
    );
  }

  return (
    <View style={styles.multiCar}>
      <Carousel
        ref={carouselRef}
        width={itemWidth}
        height={CARD_HEIGHT}
        style={{ width: screenWidth }}
        data={cars}
        loop={false}
        pagingEnabled
        snapEnabled
        onProgressChange={progress}
        renderItem={({ item, index }) => (
          <View
            style={{
              transform: [
                { translateX: index === 0 ? CARD_INSET : centeredOffset },
              ],
            }}
          >
            <Animated.View
              style={[
                styles.item,
                nudgeStyle,
                {
                  marginLeft: index === 0 ? 0 : HALF_GAP,
                  marginRight:
                    index === 0
                      ? CARD_GAP - (centeredOffset - CARD_INSET) - HALF_GAP
                      : HALF_GAP,
                },
              ]}
            >
              <MapCarCard car={item} onPress={() => onCarPress(item)} />
            </Animated.View>
          </View>
        )}
      />
      <Pagination.Basic
        progress={dotsProgress}
        data={dotsData}
        dotStyle={{
          backgroundColor: theme.colors.paginationDots.empty,
          borderRadius: 50,
        }}
        activeDotStyle={{
          backgroundColor: theme.colors.paginationDots.filled,
        }}
        size={8}
        containerStyle={styles.dots}
      />
    </View>
  );
};

const stylesheet = createStyleSheet(() => ({
  singleCard: {
    paddingHorizontal: CARD_INSET,
    marginTop: 16,
  },
  multiCar: {
    marginTop: 16,
  },
  item: {
    height: CARD_HEIGHT,
  },
  dots: {
    gap: 4,
    marginTop: 16,
    alignSelf: 'center',
  },
}));
